import React, {useEffect, useRef, useState} from 'react'
import {cdrUserLabel} from '../core/cdrUserLabel'
import {useIntl} from 'react-intl'
import {useParams} from 'react-router-dom'
import {KTSVG} from '../../../template/helpers'
import {cdrAPI} from '../../../services/CDRService'
import CreatableSelect from 'react-select/creatable'
import {Button, Overlay, Popover} from 'react-bootstrap'
import Select, {SingleValue, MultiValue} from 'react-select'
import {columnMeta, dataTableConfig} from '../config/data-table'
import {updateQuery, resetQuery} from '../slices/cdrFilterSlice'
import {useAppDispatch, useAppSelector} from '../../../hooks/redux'
import {FilterRule, DataCDR, ColumnMeta, ICDRUser} from '../core/_models'
import {shallowEqual} from 'react-redux'
import {StatusTypes, Option} from '../../types/models'
import {AiFilters, AiFiltersHandle} from './ai/AiFilters'
import {useAccount} from '../../../hooks/useAccount'
import {agentEmployeeOperators, isAgentEmployeeFilter, sanitizeAgentEmployeeRule} from '../core/agentEmployeeFilter'

const genId = () => Math.random().toString(36).slice(2)

export function CDRFilter() {
  const intl = useIntl()
  const {appId} = useParams()
  const dispatch = useAppDispatch()
  const [show, setShow] = useState(false)
  const target = useRef<HTMLButtonElement | null>(null)
  const [filters, setFilters] = useState<FilterRule[]>([])
  const [selectedColumn, setSelectedColumn] = useState<SingleValue<Option<keyof DataCDR>>>(null)
  const [activeTab, setActiveTab] = useState<'cdr' | 'llm'>('cdr')
  const [llmReady, setLlmReady] = useState(false)
  const llmRef = useRef<AiFiltersHandle>(null)
  const {data: cdrUsers} = cdrAPI.useGetUsersQuery(appId!)
  const {data: cdrLines} = cdrAPI.useGetLinesQuery(appId!)
  const {isAgent} = useAccount()
  const query = useAppSelector((state) => state.cdrFilter.query, shallowEqual)
  const llmCount = (query?.rules_llm?.items?.length ?? 0) + (query?.rules_llm?.prompt_id ? 1 : 0)

  useEffect(() => {
    if (show) setActiveTab('cdr')
  }, [show])

  useEffect(() => {
    if (query?.rules?.items.length) {
      const savedFilters = query.rules.items
        .map((item) => {
          const meta = columnMeta.find((c) => c.key === item.column)
          if (!meta) {
            return null
          }

          const ops = dataTableConfig[meta.type]
          if (!ops) {
            return null
          }

          const opItem = ops.find((o) => o.value === item.operator)
          if (!opItem) {
            return null
          }

          const rule = {
            id: item.id,
            column: item.column,
            type: meta.type,
            operator: opItem.value,
            value: item.value,
          } as FilterRule
          return isAgentEmployeeFilter(isAgent, rule.column) && cdrUsers
            ? sanitizeAgentEmployeeRule(rule, cdrUsers.users.map(cdrUserLabel))
            : rule
        })
        .filter(Boolean) as FilterRule[]

      setFilters(savedFilters)
    }
  }, [query, isAgent, cdrUsers])

  useEffect(() => {}, [filters])

  const statusLabels: Record<StatusTypes, string> = {
    ANSWERED: intl.formatMessage({id: 'CDR.STATUS.ANSWERED'}),
    NO_ANSWER: intl.formatMessage({id: 'CDR.STATUS.NO ANSWER'}),
    BUSY: intl.formatMessage({id: 'CDR.STATUS.BUSY'}),
    FAILED: intl.formatMessage({id: 'CDR.STATUS.FAILED'}),
    CANCELLED: intl.formatMessage({id: 'CDR.STATUS.CANCELLED'}),
  }

  const multiSelectOptions: Record<'status', Option<StatusTypes>[]> = {
    status: (['ANSWERED', 'NO_ANSWER', 'BUSY', 'FAILED', 'CANCELLED'] as StatusTypes[]).map((s) => ({
      label: statusLabels[s],
      value: s,
    })),
  }

  const addFilter = (column: keyof DataCDR) => {
    const meta = columnMeta.find((c) => c.key === column)
    if (!meta) return

    const firstOperator = dataTableConfig[meta.type][0]

    setFilters((p) => [
      ...p,
      {
        id: genId(),
        column: meta.key,
        type: meta.type,
        operator: firstOperator.value,
        value: meta.type === 'multi-select' ? [] : meta.type === 'boolean' ? true : '',
      },
    ])
  }

  const updateFilter = (id: string, patch: Partial<FilterRule>) => {
    setFilters((p) => p.map((f) => (f.id === id ? {...f, ...patch} : f)))
  }

  const removeFilter = (id: string) => {
    setFilters((p) => p.filter((f) => f.id !== id))
    if (filters.length === 1) {
      dispatch(resetQuery())
    }
  }

  const removeAllFilters = () => {
    setFilters([])
    dispatch(resetQuery())
    setShow(false)
  }

  const isMultiValueOperator = (op: string) => op === 'inArray' || op === 'notInArray'

  const availableColumns = columnMeta.filter((c) => !filters.some((f) => f.column === c.key))

  const applyFilters = () => {
    const rulesPayload = filters.map(
      ({id, column, operator, value}) =>
        ({
          id,
          column,
          operator: operator,
          value,
        } as FilterRule)
    )

    dispatch(
      updateQuery({
        ...query,
        offset: 0,
        rules: {
          operator: '$and',
          items: rulesPayload,
        },
      })
    )
    setShow(false)
  }

  const optionHandler = (meta: ColumnMeta): {name: string}[] => {
    if (meta.key === 'did_number') {
      return cdrLines?.lines ?? []
    }

    if (meta.key === 'to_') {
      return cdrUsers
        ? cdrUsers.users.map((u: ICDRUser) => ({
            name: cdrUserLabel(u),
          }))
        : []
    }

    return []
  }

  return (
    <>
      <Button
        ref={target}
        size='sm'
        className='btn btn-outline btn-outline-dashed btn-outline-primary btn-active-light-primary'
        onClick={() => setShow((v) => !v)}
      >
        <i className='bi bi-filter fs-5' />
        Фильтры
        {filters.length + llmCount >= 1 && (
          <span className='badge badge-success ms-2'>{filters.length + llmCount}</span>
        )}
      </Button>

      <Overlay
        target={target.current}
        show={show}
        placement='bottom-start'
        rootClose
        onHide={() => setShow(false)}
        popperConfig={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [-250, 8],
              },
            },
          ],
        }}
      >
        <Popover style={{minWidth: 900, maxWidth: 1200}}>
          <Popover.Header as='div' className='d-flex align-items-center py-1 justify-content-between'>
            Фильтры
            <button className='btn btn-sm btn-icon btn-active-color-primary' onClick={() => setShow(false)}>
              <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-1' />
            </button>
          </Popover.Header>

          <Popover.Body>
            <div className='d-flex justify-content-center bg-light-primary rounded-1 mb-3'>
              <ul className='nav nav-tabs nav-line-tabs nav-stretch fs-6 border-0 my-1'>
                <li className='nav-item'>
                  <a
                    className='nav-link active'
                    data-bs-toggle='tab'
                    href='#tab_records_filters'
                    onClick={() => setActiveTab('cdr')}
                  >
                    <span className='small text-gray-900'>Фильтры вызовов</span>
                    {filters.length >= 1 && <span className='badge badge-success ms-2'>{filters.length}</span>}
                  </a>
                </li>
                <li className='nav-item'>
                  <a
                    className='nav-link'
                    data-bs-toggle='tab'
                    href='#tab_llm_filters'
                    onClick={() => setActiveTab('llm')}
                  >
                    <span className='small text-gray-900'>Фильтры AI анализа</span>
                    {llmCount >= 1 && <span className='badge badge-success ms-2'>{llmCount}</span>}
                  </a>
                </li>
              </ul>
            </div>
            <div className='tab-content flex-column-fluid' style={{minHeight: 0, flex: '1 1 auto'}}>
              <div className='tab-pane fade show active' id='tab_records_filters' role='tabpanel'>
                <div className='h-100 d-flex flex-column'>
                  {filters.map((f, i) => {
                    const meta = columnMeta.find((c) => c.key === f.column)!
                    const multiValue = Array.isArray(f.value) ? f.value : []
                    const boolValue = typeof f.value === 'boolean' ? f.value : true
                    const agentEmployee = isAgentEmployeeFilter(isAgent, f.column)
                    const operators = agentEmployee
                      ? agentEmployeeOperators(dataTableConfig[f.type])
                      : dataTableConfig[f.type]

                    return (
                      <div key={f.id} className='align-items-center' style={{display: 'flex', gap: 6, marginBottom: 6}}>
                        {i === 0 ? (
                          <span className='badge badge-light-primary min-w-50px justify-content-center'>где</span>
                        ) : (
                          <span className='min-w-50px' />
                        )}
                        <Select
                          className='react-select-styled react-select-solid min-w-200px'
                          classNamePrefix='react-select'
                          options={[]}
                          menuIsOpen={false}
                          value={{label: meta.label, value: meta.key}}
                          isDisabled={true}
                          styles={{
                            indicatorsContainer: () => ({display: 'none'}),
                            indicatorSeparator: () => ({display: 'none'}),
                          }}
                        />
                        <Select
                          className='react-select-styled react-select-solid'
                          classNamePrefix='react-select'
                          options={operators}
                          value={operators.find((o) => o.value === f.operator)}
                          onChange={(v: SingleValue<Option<string>>) => v && updateFilter(f.id, {operator: v.value})}
                        />
                        <div style={{flex: 1}}>
                          {f.type === 'string' && !isMultiValueOperator(f.operator) && (
                            <input
                              id={f.id}
                              className='form-control form-control-solid min-w-200px'
                              value={f.value as string}
                              placeholder='Введите значение'
                              onChange={(e) => updateFilter(f.id, {value: e.target.value})}
                            />
                          )}

                          {isMultiValueOperator(f.operator) && f.type !== 'multi-select' && (
                            <CreatableSelect<Option<string>, true>
                              key={f.id}
                              isMulti
                              className='react-select-multi-custom react-select-solid'
                              classNamePrefix='react-select'
                              options={optionHandler(meta).map((o: any) => ({label: o.name, value: o.name}))}
                              isValidNewOption={agentEmployee ? () => false : undefined}
                              placeholder={
                                agentEmployee ? 'Выберите сотрудника' : 'Введите значение и нажмите Enter или Tab'
                              }
                              value={multiValue.map((v) => ({
                                label: f.column === 'status' ? statusLabels[v as StatusTypes] : v,
                                value: v,
                              }))}
                              onChange={(v: MultiValue<Option<string>>) =>
                                updateFilter(f.id, {value: v.map((o) => o.value)})
                              }
                            />
                          )}

                          {f.type === 'number' && f.operator !== 'isBetween' && (
                            <input
                              id={f.id}
                              type='number'
                              min={0}
                              max={3600}
                              placeholder='Введите значение'
                              className='form-control'
                              value={f.value as number}
                              onChange={(e) => updateFilter(f.id, {value: Number(e.target.value)})}
                            />
                          )}

                          {f.type === 'number' && f.operator === 'isBetween' && (
                            <div className='d-flex gap-2 align-items-center'>
                              <input
                                type='number'
                                className='form-control'
                                placeholder='от'
                                min={0}
                                max={3600}
                                value={(f.value as any)?.from ?? ''}
                                onChange={(e) =>
                                  updateFilter(f.id, {
                                    value: {
                                      ...(f.value as any),
                                      from: Number(e.target.value),
                                    },
                                  })
                                }
                              />
                              <span className='text-muted'>—</span>
                              <input
                                type='number'
                                className='form-control'
                                placeholder='до'
                                min={0}
                                max={3600}
                                value={(f.value as any)?.to ?? ''}
                                onChange={(e) =>
                                  updateFilter(f.id, {
                                    value: {
                                      ...(f.value as any),
                                      to: Number(e.target.value),
                                    },
                                  })
                                }
                              />
                            </div>
                          )}

                          {f.type === 'boolean' && (
                            <Select<Option<boolean>>
                              className='react-select-styled react-select-solid'
                              classNamePrefix='react-select'
                              options={[
                                {label: 'Да', value: true},
                                {label: 'Нет', value: false},
                              ]}
                              value={{
                                label: boolValue ? 'Да' : 'Нет',
                                value: boolValue,
                              }}
                              onChange={(v: SingleValue<Option<boolean>>) => v && updateFilter(f.id, {value: v.value})}
                            />
                          )}

                          {f.type === 'multi-select' && (
                            <Select<Option<string>, true>
                              isMulti
                              className='react-select-multi-custom react-select-solid'
                              classNamePrefix='react-select'
                              placeholder='Введите значение'
                              options={multiSelectOptions.status}
                              value={(f.value as string[]).map((v) => ({
                                label: statusLabels[v as StatusTypes],
                                value: v,
                              }))}
                              onChange={(v: MultiValue<Option<string>>) =>
                                updateFilter(f.id, {value: v.map((o) => o.value)})
                              }
                            />
                          )}
                        </div>
                        <Button size='sm' variant='outline-danger' onClick={() => removeFilter(f.id)}>
                          ✕
                        </Button>
                      </div>
                    )
                  })}

                  {availableColumns.length > 0 && (
                    <Select
                      className='react-select-styled react-select-solid py-2'
                      classNamePrefix='react-select'
                      placeholder='Добавить фильтр'
                      value={selectedColumn}
                      options={availableColumns.map((c) => ({
                        label: c.label,
                        value: c.key,
                      }))}
                      onChange={(v: SingleValue<Option<keyof DataCDR>>) => {
                        if (!v) return
                        addFilter(v.value)
                        setSelectedColumn(null)
                      }}
                    />
                  )}
                </div>
              </div>
              <div className='tab-pane fade h-100' id='tab_llm_filters' role='tabpanel'>
                <div className='h-100 d-flex flex-column'>
                  <AiFilters ref={llmRef} onSelectionChange={setLlmReady} />
                </div>
              </div>
            </div>
            <div className='d-flex align-items-center mt-4 gap-2'>
              <Button
                size='sm'
                className='w-100 btn btn-outline btn-outline-dashed btn-outline-primary btn-active-light-primary'
                onClick={() => {
                  if (activeTab === 'cdr') {
                    removeAllFilters()
                  } else {
                    llmRef.current?.reset()
                  }
                }}
                disabled={activeTab === 'cdr' ? filters.length < 1 : !llmReady}
              >
                Сбросить фильтры
              </Button>
              <Button
                size='sm'
                className='w-100'
                onClick={() => {
                  if (activeTab === 'cdr') {
                    applyFilters()
                  } else {
                    llmRef.current?.apply()
                    setShow(false)
                  }
                }}
                disabled={activeTab === 'cdr' ? filters.length < 1 : !llmReady}
              >
                Применить
              </Button>
            </div>
          </Popover.Body>
        </Popover>
      </Overlay>
    </>
  )
}
